/**
 * React Hook für Offline-Queue Integration
 *
 * Bietet:
 * - Queue Status (pendingCount, isSyncing, hasConflicts)
 * - Methoden zum Queuen und Synchronisieren
 * - Konflikt-Handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { offlineQueueService, QueueStatus } from '../services/offlineQueueService';
import { offlineDb, STORES } from '../services/offlineDatabase';

/**
 * Hook für Offline-Queue Funktionalität
 */
export const useOfflineQueue = () => {
  const [state, setState] = useState({
    pendingCount: 0,
    isSyncing: false,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    hasConflicts: false,
    conflicts: [],
    lastSyncTime: null,
    failedCount: 0
  });

  const isMounted = useRef(true);

  // Aktualisiert den State basierend auf Queue-Daten
  const updateQueueState = useCallback(async () => {
    if (!isMounted.current) return;

    try {
      const allItems = await offlineDb.getAll(STORES.QUEUE);

      const pending = allItems.filter(i =>
        i.status === QueueStatus.PENDING || i.status === QueueStatus.SYNCING
      );
      const conflicts = allItems.filter(i => i.status === QueueStatus.CONFLICT);
      const failed = allItems.filter(i => i.status === QueueStatus.FAILED);

      setState(prev => ({
        ...prev,
        pendingCount: pending.length,
        hasConflicts: conflicts.length > 0,
        conflicts: conflicts,
        failedCount: failed.length
      }));
    } catch (error) {
      console.error('[useOfflineQueue] Fehler beim Aktualisieren des States:', error);
    }
  }, []);

  // Subscribe zu Queue-Events
  useEffect(() => {
    isMounted.current = true;

    // Initialer State
    updateQueueState();

    // Event-Listener
    const unsubscribe = offlineQueueService.subscribe((event) => {
      if (!isMounted.current) return;

      switch (event.type) {
        case 'QUEUE_UPDATED':
        case 'ITEM_SYNCED':
        case 'ITEM_FAILED':
        case 'CONFLICT_RESOLVED':
        case 'QUEUE_CLEARED':
          updateQueueState();
          break;

        case 'SYNC_STARTED':
          setState(prev => ({ ...prev, isSyncing: true }));
          break;

        case 'SYNC_COMPLETED':
          setState(prev => ({
            ...prev,
            isSyncing: false,
            lastSyncTime: Date.now()
          }));
          updateQueueState();
          break;

        case 'ONLINE':
          setState(prev => ({ ...prev, isOnline: true }));
          break;

        case 'OFFLINE':
          setState(prev => ({ ...prev, isOnline: false }));
          break;

        case 'CONFLICT_DETECTED':
          updateQueueState();
          break;

        default:
          break;
      }
    });

    // Browser online/offline Events
    const handleOnline = () => setState(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setState(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      isMounted.current = false;
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateQueueState]);

  /**
   * Fügt eine Operation zur Queue hinzu
   */
  const queueOperation = useCallback(async (operation) => {
    return await offlineQueueService.enqueue(operation);
  }, []);

  /**
   * Erzwingt eine sofortige Synchronisation
   */
  const forceSync = useCallback(async () => {
    if (!state.isOnline) {
      console.log('[useOfflineQueue] Kann nicht synchronisieren - offline');
      return false;
    }
    return await offlineQueueService.forceSync();
  }, [state.isOnline]);

  /**
   * Löst einen Konflikt auf
   * @param {number} conflictId - ID des Queue-Items
   * @param {'LOCAL'|'SERVER'|'MERGE'} strategy
   * @param {object} mergedData - Optional: Zusammengeführte Daten für MERGE
   */
  const resolveConflict = useCallback(async (conflictId, strategy, mergedData = null) => {
    await offlineQueueService.resolveConflict(conflictId, strategy, mergedData);
    await updateQueueState();
  }, [updateQueueState]);

  /**
   * Holt Details zu einem bestimmten Konflikt
   */
  const getConflictDetails = useCallback(async (conflictId) => {
    return await offlineDb.get(STORES.QUEUE, conflictId);
  }, []);

  /**
   * Leert die Queue (für Logout)
   */
  const clearQueue = useCallback(async () => {
    await offlineQueueService.clearQueue();
  }, []);

  /**
   * Prüft ob eine Entity ausstehende Änderungen hat
   */
  const hasPendingChanges = useCallback(async (entityType, entityId) => {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    return allItems.some(item =>
      (item.entityType === entityType && item.entityId === entityId) ||
      (item.entityType === entityType && item.parentId === entityId)
    );
  }, []);

  return {
    // State
    ...state,

    // Computed
    hasPendingOperations: state.pendingCount > 0,
    needsAttention: state.hasConflicts || state.failedCount > 0,

    // Methoden
    queueOperation,
    forceSync,
    resolveConflict,
    getConflictDetails,
    clearQueue,
    hasPendingChanges,
    refreshState: updateQueueState
  };
};

export default useOfflineQueue;
